import React, { useState, useEffect } from 'react'

import i18n from 'live/services/i18n'
import moment from 'moment'

import { NewReportCircle, Table, TableContainer } from './raListTableStyles'
import { TDContent } from './TDContent/TDContent'
import { ReportsApprovalTypes } from '../../../types/ReportsApproval'
import {
    ReportsApprovalColumns,
    SortOption,
    handleChangeSorter,
    handleSorterReportsApprovalList
} from './helper/helper'
import { LoadMoreButton } from './LeadMoreButton/LoadMoreButton'
import { formatAssetNameByRegex, mountFullDateAccordingToLocale } from '../../../utils/utils'

interface RAListTableProps {
    reportsApproval: ReportsApprovalTypes[]
    filteredReportsApproval: ReportsApprovalTypes[]
    selectedSorter: SortOption
    setSelectedSorter: (newSorter: SortOption) => void
    reportsLimit: number
    setReportsLimit: (reportsLimit: number) => void
    openCheckModalAccordingToType: (buttonType: 'data' | 'send', schedulerId: number) => void
}

export function RAListTable({
    reportsApproval,
    filteredReportsApproval,
    selectedSorter,
    setSelectedSorter,
    reportsLimit,
    setReportsLimit,
    openCheckModalAccordingToType
}: RAListTableProps): JSX.Element {
    const [reportsApprovalBeingDisplayed, setReportsApprovalBeingDisplayed] = useState(
        filteredReportsApproval.slice(0, reportsLimit).length
    )
    const [loading, setLoading] = useState(false)

    function loadMoreReportsApproval() {
        const totalReportsApproval = [...reportsApproval]

        if (totalReportsApproval.length > 0) {
            if (reportsApprovalBeingDisplayed < totalReportsApproval.length) {
                const nextPage =
                    totalReportsApproval.length - reportsApprovalBeingDisplayed > 50
                        ? 50
                        : totalReportsApproval.length - reportsApprovalBeingDisplayed
                setLoading(true)

                setTimeout(() => {
                    setReportsLimit(prevState => prevState + nextPage)
                    setReportsApprovalBeingDisplayed(prevState => prevState + nextPage)

                    setLoading(false)
                }, 500)
            }
        }
    }

    useEffect(() => {
        setReportsApprovalBeingDisplayed(filteredReportsApproval.slice(0, reportsLimit).length)
    }, [filteredReportsApproval])

    return (
        <TableContainer>
            <Table>
                <thead>
                    <tr>
                        <th>
                            <a
                                onClick={() =>
                                    handleChangeSorter(
                                        ReportsApprovalColumns.REPORT_NAME,
                                        selectedSorter,
                                        setSelectedSorter
                                    )
                                }
                            >
                                {i18n(ReportsApprovalColumns.REPORT_NAME)}
                                {selectedSorter.field === ReportsApprovalColumns.REPORT_NAME ? (
                                    <i
                                        className={`fa fa-arrow-${selectedSorter.reverse ? 'down' : 'up'}`}
                                        style={{ marginLeft: '6px', fontSize: 9.5 }}
                                    />
                                ) : null}
                            </a>
                        </th>
                        <th>
                            <a
                                onClick={() =>
                                    handleChangeSorter(
                                        ReportsApprovalColumns.SCHEDULE_NAME,
                                        selectedSorter,
                                        setSelectedSorter
                                    )
                                }
                            >
                                {i18n(ReportsApprovalColumns.SCHEDULE_NAME)}
                                {selectedSorter.field === ReportsApprovalColumns.SCHEDULE_NAME ? (
                                    <i
                                        className={`fa fa-arrow-${selectedSorter.reverse ? 'down' : 'up'}`}
                                        style={{ marginLeft: '6px', fontSize: 9.5 }}
                                    />
                                ) : null}
                            </a>
                        </th>
                        <th>
                            <a
                                onClick={() =>
                                    handleChangeSorter(
                                        ReportsApprovalColumns.ALARM_RULE,
                                        selectedSorter,
                                        setSelectedSorter
                                    )
                                }
                            >
                                {i18n(ReportsApprovalColumns.ALARM_RULE)}
                                {selectedSorter.field === ReportsApprovalColumns.ALARM_RULE ? (
                                    <i
                                        className={`fa fa-arrow-${selectedSorter.reverse ? 'down' : 'up'}`}
                                        style={{ marginLeft: '6px', fontSize: 9.5 }}
                                    />
                                ) : null}
                            </a>
                        </th>
                        <th>
                            <a
                                onClick={() =>
                                    handleChangeSorter(ReportsApprovalColumns.ASSET, selectedSorter, setSelectedSorter)
                                }
                            >
                                {i18n(ReportsApprovalColumns.ASSET)}
                                {selectedSorter.field === ReportsApprovalColumns.ASSET ? (
                                    <i
                                        className={`fa fa-arrow-${selectedSorter.reverse ? 'down' : 'up'}`}
                                        style={{ marginLeft: '6px', fontSize: 9.5 }}
                                    />
                                ) : null}
                            </a>
                        </th>
                        <th>
                            <a
                                onClick={() =>
                                    handleChangeSorter(
                                        ReportsApprovalColumns.TIMESPAN,
                                        selectedSorter,
                                        setSelectedSorter
                                    )
                                }
                            >
                                {i18n(ReportsApprovalColumns.TIMESPAN)}
                                {selectedSorter.field === ReportsApprovalColumns.TIMESPAN ? (
                                    <i
                                        className={`fa fa-arrow-${selectedSorter.reverse ? 'down' : 'up'}`}
                                        style={{ marginLeft: '6px', fontSize: 9.5 }}
                                    />
                                ) : null}
                            </a>
                        </th>
                        <th>
                            <a
                                onClick={() =>
                                    handleChangeSorter(
                                        ReportsApprovalColumns.DATA_STATUS,
                                        selectedSorter,
                                        setSelectedSorter
                                    )
                                }
                            >
                                {i18n(ReportsApprovalColumns.DATA_STATUS)}
                                {selectedSorter.field === ReportsApprovalColumns.DATA_STATUS ? (
                                    <i
                                        className={`fa fa-arrow-${selectedSorter.reverse ? 'down' : 'up'}`}
                                        style={{ marginLeft: '6px', fontSize: 9.5 }}
                                    />
                                ) : null}
                            </a>
                        </th>
                        <th>
                            <a
                                onClick={() =>
                                    handleChangeSorter(
                                        ReportsApprovalColumns.SENDING_STATUS,
                                        selectedSorter,
                                        setSelectedSorter
                                    )
                                }
                            >
                                {i18n(ReportsApprovalColumns.SENDING_STATUS)}
                                {selectedSorter.field === ReportsApprovalColumns.SENDING_STATUS ? (
                                    <i
                                        className={`fa fa-arrow-${selectedSorter.reverse ? 'down' : 'up'}`}
                                        style={{ marginLeft: '6px', fontSize: 9.5 }}
                                    />
                                ) : null}
                            </a>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {filteredReportsApproval
                        .slice(0, reportsLimit)
                        .sort((a, b) => handleSorterReportsApprovalList(a, b, selectedSorter))
                        .map(report => {
                            const isPT_BR = moment.locale() === 'pt-br'

                            const mountedDate = mountFullDateAccordingToLocale(report.timespan)

                            return (
                                <tr key={report.id}>
                                    <td style={{ height: '106px', minHeight: '106px', padding: '20px' }}>
                                        <TDContent
                                            text={report.reportname}
                                            additionalContent={
                                                report.datastatus !== 'CHECKED' &&
                                                report.sendstatus !== 'SENT' && <NewReportCircle />
                                            }
                                            position="left"
                                        />
                                    </td>
                                    <td style={{ height: '106px', minHeight: '106px', padding: '20px' }}>
                                        <TDContent text={report.schedulename} />
                                    </td>
                                    <td style={{ height: '106px', minHeight: '106px', padding: '20px' }}>
                                        <TDContent text={`${report.alarmname} (ID - 000${report.alarmid})`} />
                                    </td>
                                    <td style={{ height: '106px', minHeight: '106px', padding: '20px' }}>
                                        <TDContent
                                            // This regex identifies the last parentheses in assets and remove, leaving only the asset name type id. E.g. from well 2 (Well) (2) to well 2 (Well)
                                            text={formatAssetNameByRegex(report.asset, /\s\((\w+)\)$/)}
                                        />
                                    </td>
                                    <td style={{ height: '106px', minHeight: '106px', padding: '20px' }}>
                                        <TDContent text={mountedDate} />
                                    </td>
                                    <td style={{ height: '106px', minHeight: '106px', padding: '20px' }}>
                                        <TDContent
                                            text={
                                                report.datastatus === 'UNCHECKED'
                                                    ? i18n('Unchecked')
                                                    : report.datastatus === 'DASHBOARD_NOT_FOUND'
                                                    ? i18n('Dashboard not found')
                                                    : i18n('Data checked on') +
                                                      moment(report.lastdatecheckdata).format(
                                                          `${isPT_BR ? 'DD/MM/YYYY' : 'YYYY/MM/DD'} LT`
                                                      ) +
                                                      i18n('Checked by') +
                                                      report.usernamecheckdata
                                            }
                                            additionalContent={
                                                <button
                                                    className="check-btn"
                                                    onClick={() => {
                                                        openCheckModalAccordingToType('data', report.id)
                                                    }}
                                                >
                                                    {i18n('Check data')}
                                                </button>
                                            }
                                        />
                                    </td>
                                    <td style={{ height: '106px', minHeight: '106px', padding: '20px' }}>
                                        <TDContent
                                            text={
                                                report.sendstatus === 'NOT_SEND'
                                                    ? i18n('Not sent')
                                                    : i18n('Last sent on') +
                                                      moment(report.lastdatesenddata).format(
                                                          `${isPT_BR ? 'DD/MM/YYYY' : 'YYYY/MM/DD'} LT`
                                                      ) +
                                                      i18n('Sent by') +
                                                      report.usernamesenddata
                                            }
                                            additionalContent={
                                                <button
                                                    className="sending-btn"
                                                    onClick={() => openCheckModalAccordingToType('send', report.id)}
                                                >
                                                    {i18n('Send report')}
                                                </button>
                                            }
                                        />
                                    </td>
                                </tr>
                            )
                        })}
                </tbody>
            </Table>

            <LoadMoreButton
                totalReportsApproval={reportsApproval.length}
                reportsApprovalBeingDisplayed={reportsApprovalBeingDisplayed}
                loadMoreReportsApproval={loadMoreReportsApproval}
                loading={loading}
            />
        </TableContainer>
    )
}
